import React from "react";
import { createPortal } from "react-dom";
import { Box, Tooltip } from "@mui/material";
import { Draggable } from "@hello-pangea/dnd";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getAvatarColor, getInitials } from "../shared/AssigneeAvatarStack";

// Workload thresholds — spec: amber < 80%, red >= 80%
// We use a simple count threshold: <=3 = amber (moderate), >=4 = red (heavy)
const WORKLOAD_AMBER = "#E8A817"; /* spec: priority-high / moderate load */
const WORKLOAD_RED = "#D4572B"; /* spec: priority-critical / heavy load */

const getWorkloadColor = (openCount: number): string => {
  if (openCount === 0) return "#E8E5DF"; /* spec: border — no workload, subtle */
  if (openCount <= 3) return WORKLOAD_AMBER;
  return WORKLOAD_RED;
};

interface SidebarMemberRowProps {
  userId: string;
  userName: string;
  openCount: number;
  maxOpenInTeam: number;
  draggableIndex: number;
  teamId: string;
}

const SidebarMemberRow: React.FC<SidebarMemberRowProps> = ({
  userId,
  userName,
  openCount,
  maxOpenInTeam,
  draggableIndex,
  teamId,
}) => {
  const { translate } = useTranslation("operations");
  const color = getAvatarColor(userId);
  const workloadColor = getWorkloadColor(openCount);
  const barWidth = maxOpenInTeam > 0
    ? Math.min((openCount / maxOpenInTeam) * 100, 100)
    : 0;

  return (
    <Draggable draggableId={`person::${teamId}::${userId}`} index={draggableIndex}>
      {(provided, snapshot) => {
        const draggableElement = (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={{
              padding: "5px 8px 5px 20px", /* spec: member row padding, indented */
              display: "flex",
              flexDirection: "column",
              gap: "3px",
              cursor: snapshot.isDragging ? "grabbing" : "grab",
              transition: snapshot.isDragging ? "none" : "background-color 0.15s ease",
              transform: snapshot.isDragging ? "rotate(2deg)" : "none",
              boxShadow: snapshot.isDragging
                ? "0 8px 24px rgba(0,0,0,0.15)"
                : "none",
              opacity: snapshot.isDragging ? 0.92 : 1,
              backgroundColor: snapshot.isDragging ? "#FFFFFF" : "transparent",
              borderRadius: snapshot.isDragging ? "6px" : "6px",
              zIndex: snapshot.isDragging ? 9999 : undefined,
              "&:hover": snapshot.isDragging
                ? {}
                : {
                    backgroundColor: "#F7F5F0", /* spec: input-bg hover */
                    "& .drag-grip": { opacity: 1 },
                  },
            }}
          >
            {/* Top row: avatar + name + workload — spec: 8px gap */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px", /* spec: member row gap */
              }}
            >
              {/* Avatar circle — spec: 20px sidebar avatar */}
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "8px", /* spec: 8px for 20px circle */
                  fontWeight: 600,
                  fontFamily: dt.font,
                  color: "#FFFFFF",
                  flexShrink: 0,
                }}
              >
                {getInitials(userName)}
              </Box>

              {/* Name — spec: Sora 12px/400 text-body */}
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#4A4F57", /* spec: text-body */
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {userName}
              </Box>

              {/* Workload indicator: count + bar — spec: 40px bar, 3px tall */}
              <Tooltip title={translate("sidebar.dragToAssign")} enterDelay={600}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  {/* Count — spec: JetBrains Mono 10px/500 text-body */}
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "10px",
                      fontWeight: 500,
                      color: "#4A4F57", /* spec: text-body */
                      lineHeight: 1,
                    }}
                  >
                    {openCount}
                  </Box>
                  {/* Workload bar — spec: 40px wide, 3px tall, track #F2F0EB */}
                  <Box
                    sx={{
                      width: "40px",
                      height: "3px",
                      backgroundColor: "#F2F0EB", /* spec: board-bg / track */
                      borderRadius: "2px",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(barWidth, openCount > 0 ? 10 : 0)}%`,
                        height: "100%",
                        backgroundColor: workloadColor,
                        borderRadius: "2px",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            </Box>
          </Box>
        );

        // Portal fix: render dragging element in document.body to escape
        // parent transforms, overflow, and position contexts
        if (snapshot.isDragging) {
          return createPortal(draggableElement, document.body);
        }

        return draggableElement;
      }}
    </Draggable>
  );
};

export { getWorkloadColor };
export default SidebarMemberRow;
